import { useCallback } from 'react'
import { distanciaPuntoALinea, distanciaPuntoACirculo, normalizarCoordenadas } from '../utils/canvasUtils'
import { obtenerCoordenadasMedida, calcularCoordenadasEtiqueta } from '../utils/geometriaUtils'

/**
 * Hook para manejar las interacciones del canvas (clicks, drag, hover)
 */
export const useCanvasInteractions = ({
  canvasRef,
  coordenadas,
  medidasManuales,
  medidaSeleccionada,
  lineaReferenciaSeleccionada,
  isDragging,
  arrastrandoPunto,
  modoDibujo,
  medidaIndexParaLineaReferencia,
  puntoALineaReferencia,
  puntoBLineaReferenciaConfirmado,
  puntoA,
  puntoBConfirmado,
  centroCirculo,
  radioConfirmado,
  // Setters
  setMedidaSeleccionada,
  setLineaReferenciaSeleccionada,
  setArrastrandoPunto,
  setIsDragging,
  setCursorHover,
  setPuntoA,
  setPuntoBTemporal,
  setPuntoBConfirmado,
  setCentroCirculo,
  setPuntoRadioTemporal,
  setRadioConfirmado,
  setPuntoALineaReferencia,
  setPuntoBLineaReferencia,
    setPuntoBLineaReferenciaConfirmado,
    setMedidaIndexParaLineaReferencia,
    setModoDibujo,
    setCoordenadas,
    setMedidasManuales,
    inputRefs,
}) => {
  // Encontrar medida cercana
  const encontrarMedidaCercana = useCallback((x, y, margen = 5) => {
    const canvas = canvasRef.current
    if (!canvas) return null

    const escalaX = canvas.width / 1000
    const escalaY = canvas.height / 1000
    const todasLasCoordenadas = [...coordenadas, ...medidasManuales]

    for (let index = 0; index < todasLasCoordenadas.length; index++) {
      const cota = todasLasCoordenadas[index]
      
      // Buscar en lineas_a_repuesto primero
      if (cota.lineas_a_repuesto && Array.isArray(cota.lineas_a_repuesto)) {
        for (const linea of cota.lineas_a_repuesto) {
          if (linea && linea.x_ini !== undefined && linea.y_ini !== undefined && 
              linea.x_fin !== undefined && linea.y_fin !== undefined) {
            const x1 = linea.x_ini * escalaX
            const y1 = linea.y_ini * escalaY
            const x2 = linea.x_fin * escalaX
            const y2 = linea.y_fin * escalaY
            
            const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
            if (distancia <= margen) {
              return index
            }
          }
        }
      }
      
      // Buscar en geometria_cota
      const coords = obtenerCoordenadasMedida(cota)
      if (coords) {
        if (coords.tipo === 'linea') {
          const x1 = coords.x1 * escalaX
          const y1 = coords.y1 * escalaY
          const x2 = coords.x2 * escalaX
          const y2 = coords.y2 * escalaY
          
          const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
          if (distancia <= margen) {
            return index
          }
        } else if (coords.tipo === 'circulo') {
          const cx = coords.x * escalaX
          const cy = coords.y * escalaY
          const radio = coords.radio * escalaX
          
          const distancia = distanciaPuntoACirculo(x, y, cx, cy, radio)
          if (distancia <= margen) {
            return index
          }
        }
      }
    }

    return null
  }, [coordenadas, medidasManuales, canvasRef])

  // Encontrar línea de referencia específica
  const encontrarLineaReferenciaEspecifica = useCallback((x, y, margen = 5) => {
    const canvas = canvasRef.current
    if (!canvas) return null

    const escalaX = canvas.width / 1000
    const escalaY = canvas.height / 1000
    const todasLasCoordenadas = [...coordenadas, ...medidasManuales]

    for (let medidaIndex = 0; medidaIndex < todasLasCoordenadas.length; medidaIndex++) {
      const cota = todasLasCoordenadas[medidaIndex]
      
      if (cota.lineas_a_repuesto && Array.isArray(cota.lineas_a_repuesto)) {
        for (let lineaIndex = 0; lineaIndex < cota.lineas_a_repuesto.length; lineaIndex++) {
          const linea = cota.lineas_a_repuesto[lineaIndex]
          if (linea && linea.x_ini !== undefined && linea.y_ini !== undefined && 
              linea.x_fin !== undefined && linea.y_fin !== undefined) {
            const x1 = linea.x_ini * escalaX
            const y1 = linea.y_ini * escalaY
            const x2 = linea.x_fin * escalaX
            const y2 = linea.y_fin * escalaY
            
            const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
            if (distancia <= margen) {
              return { medidaIndex, lineaIndex }
            }
          }
        }
      }
    }
    return null
  }, [coordenadas, medidasManuales, canvasRef])

  // Encontrar círculo de arrastre de línea de referencia
  const encontrarCirculoArrastre = useCallback((x, y, radioCirculo = 8) => {
    if (!lineaReferenciaSeleccionada) return null

    const canvas = canvasRef.current
    if (!canvas) return null

    const escalaX = canvas.width / 1000
    const escalaY = canvas.height / 1000
    const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
    const cota = todasLasCoordenadas[lineaReferenciaSeleccionada.medidaIndex]
    
    if (!cota || !cota.lineas_a_repuesto || !Array.isArray(cota.lineas_a_repuesto)) {
      return null
    }

    const linea = cota.lineas_a_repuesto[lineaReferenciaSeleccionada.lineaIndex]
    if (!linea) return null

    const x1 = linea.x_ini * escalaX
    const y1 = linea.y_ini * escalaY
    const x2 = linea.x_fin * escalaX
    const y2 = linea.y_fin * escalaY

    const distInicio = Math.sqrt((x - x1) ** 2 + (y - y1) ** 2)
    if (distInicio <= radioCirculo) {
      return 'inicio'
    }

    const distFin = Math.sqrt((x - x2) ** 2 + (y - y2) ** 2)
    if (distFin <= radioCirculo) {
      return 'fin'
    }

    return null
  }, [lineaReferenciaSeleccionada, coordenadas, medidasManuales, canvasRef])

  // Encontrar círculo de arrastre de medida
  const encontrarCirculoArrastreMedida = useCallback((x, y, radioCirculo = 8) => {
    if (medidaSeleccionada === null) return null

    const canvas = canvasRef.current
    if (!canvas) return null

    const escalaX = canvas.width / 1000
    const escalaY = canvas.height / 1000
    const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
    const cota = todasLasCoordenadas[medidaSeleccionada]
    
    if (!cota) return null

    const coords = obtenerCoordenadasMedida(cota)
    if (!coords || coords.tipo !== 'linea') return null

    const x1 = coords.x1 * escalaX
    const y1 = coords.y1 * escalaY
    const x2 = coords.x2 * escalaX
    const y2 = coords.y2 * escalaY

    const distInicio = Math.sqrt((x - x1) ** 2 + (y - y1) ** 2)
    if (distInicio <= radioCirculo) {
      return 'inicio'
    }

    const distFin = Math.sqrt((x - x2) ** 2 + (y - y2) ** 2)
    if (distFin <= radioCirculo) {
      return 'fin'
    }

    return null
  }, [medidaSeleccionada, coordenadas, medidasManuales, canvasRef])

  // Handler para arrastrar
  const handleMouseMoveCanvasDrag = useCallback((e) => {
    if (!isDragging || !arrastrandoPunto) return
    if (!lineaReferenciaSeleccionada && medidaSeleccionada === null) return

    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const { x: xNormalizado, y: yNormalizado } = normalizarCoordenadas(x, y, canvas)

    // Arrastrar línea de referencia
    if (lineaReferenciaSeleccionada) {
      const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
      const cota = todasLasCoordenadas[lineaReferenciaSeleccionada.medidaIndex]
      
      if (!cota || !cota.lineas_a_repuesto || !Array.isArray(cota.lineas_a_repuesto)) {
        return
      }

      const esManual = lineaReferenciaSeleccionada.medidaIndex >= coordenadas.length

      if (esManual) {
        const indexManual = lineaReferenciaSeleccionada.medidaIndex - coordenadas.length
        setMedidasManuales(prev => {
          const nuevas = [...prev]
          if (!nuevas[indexManual] || !nuevas[indexManual].lineas_a_repuesto) return nuevas
          
          const nuevasLineas = [...nuevas[indexManual].lineas_a_repuesto]
          const lineaActualizada = { ...nuevasLineas[lineaReferenciaSeleccionada.lineaIndex] }
          
          if (arrastrandoPunto === 'inicio') {
            lineaActualizada.x_ini = xNormalizado
            lineaActualizada.y_ini = yNormalizado
          } else if (arrastrandoPunto === 'fin') {
            lineaActualizada.x_fin = xNormalizado
            lineaActualizada.y_fin = yNormalizado
          }
          
          nuevasLineas[lineaReferenciaSeleccionada.lineaIndex] = lineaActualizada
          
          nuevas[indexManual] = {
            ...nuevas[indexManual],
            lineas_a_repuesto: nuevasLineas
          }
          return nuevas
        })
      } else {
        setCoordenadas(prev => {
          const nuevas = [...prev]
          if (!nuevas[lineaReferenciaSeleccionada.medidaIndex] || !nuevas[lineaReferenciaSeleccionada.medidaIndex].lineas_a_repuesto) return nuevas
          
          const nuevasLineas = [...nuevas[lineaReferenciaSeleccionada.medidaIndex].lineas_a_repuesto]
          const lineaActualizada = { ...nuevasLineas[lineaReferenciaSeleccionada.lineaIndex] }
          
          if (arrastrandoPunto === 'inicio') {
            lineaActualizada.x_ini = xNormalizado
            lineaActualizada.y_ini = yNormalizado
          } else if (arrastrandoPunto === 'fin') {
            lineaActualizada.x_fin = xNormalizado
            lineaActualizada.y_fin = yNormalizado
          }
          
          nuevasLineas[lineaReferenciaSeleccionada.lineaIndex] = lineaActualizada
          
          nuevas[lineaReferenciaSeleccionada.medidaIndex] = {
            ...nuevas[lineaReferenciaSeleccionada.medidaIndex],
            lineas_a_repuesto: nuevasLineas
          }
          return nuevas
        })
      }
    }
    // Arrastrar medida
    else if (medidaSeleccionada !== null) {
      const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
      const cota = todasLasCoordenadas[medidaSeleccionada]
      
      if (!cota) return

      const esManual = medidaSeleccionada >= coordenadas.length
      const actualizarGeometria = (geometria) => {
        if (arrastrandoPunto === 'inicio') {
          geometria.x_ini = xNormalizado
          geometria.y_ini = yNormalizado
        } else if (arrastrandoPunto === 'fin') {
          geometria.x_fin = xNormalizado
          geometria.y_fin = yNormalizado
        }
        return geometria
      }

      if (esManual) {
        const indexManual = medidaSeleccionada - coordenadas.length
        setMedidasManuales(prev => {
          const nuevas = [...prev]
          if (!nuevas[indexManual]) return nuevas
          
          const cotaActualizada = { ...nuevas[indexManual] }
          
          if (cotaActualizada.geometria_cota && !Array.isArray(cotaActualizada.geometria_cota)) {
            cotaActualizada.geometria_cota = actualizarGeometria({ ...cotaActualizada.geometria_cota })
          } else if (cotaActualizada.geometria_cota && Array.isArray(cotaActualizada.geometria_cota) && cotaActualizada.geometria_cota.length > 0) {
            cotaActualizada.geometria_cota = [
              actualizarGeometria({ ...cotaActualizada.geometria_cota[0] }),
              ...cotaActualizada.geometria_cota.slice(1)
            ]
          } else if (cotaActualizada.coordenadas_normalizadas) {
            cotaActualizada.coordenadas_normalizadas = actualizarGeometria({ ...cotaActualizada.coordenadas_normalizadas })
          }
          
          // Recalcular coordenadas_etiqueta después de actualizar la geometría
          const nuevaEtiqueta = calcularCoordenadasEtiqueta(cotaActualizada)
          if (nuevaEtiqueta) {
            cotaActualizada.coordenadas_etiqueta = nuevaEtiqueta
          }
          
          nuevas[indexManual] = cotaActualizada
          return nuevas
        })
      } else {
        setCoordenadas(prev => {
          const nuevas = [...prev]
          if (!nuevas[medidaSeleccionada]) return nuevas
          
          const cotaActualizada = { ...nuevas[medidaSeleccionada] }
          
          if (cotaActualizada.geometria_cota && !Array.isArray(cotaActualizada.geometria_cota)) {
            cotaActualizada.geometria_cota = actualizarGeometria({ ...cotaActualizada.geometria_cota })
          } else if (cotaActualizada.geometria_cota && Array.isArray(cotaActualizada.geometria_cota) && cotaActualizada.geometria_cota.length > 0) {
            cotaActualizada.geometria_cota = [
              actualizarGeometria({ ...cotaActualizada.geometria_cota[0] }),
              ...cotaActualizada.geometria_cota.slice(1)
            ]
          } else if (cotaActualizada.coordenadas_normalizadas) {
            cotaActualizada.coordenadas_normalizadas = actualizarGeometria({ ...cotaActualizada.coordenadas_normalizadas })
          }
          
          // Recalcular coordenadas_etiqueta después de actualizar la geometría
          const nuevaEtiqueta = calcularCoordenadasEtiqueta(cotaActualizada)
          if (nuevaEtiqueta) {
            cotaActualizada.coordenadas_etiqueta = nuevaEtiqueta
          }
          
          nuevas[medidaSeleccionada] = cotaActualizada
          return nuevas
        })
      }
    }
  }, [isDragging, arrastrandoPunto, lineaReferenciaSeleccionada, medidaSeleccionada, coordenadas, medidasManuales, canvasRef, setCoordenadas, setMedidasManuales])

  // Handler para movimiento del mouse
  const handleMouseMoveCanvas = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (isDragging && arrastrandoPunto) {
      handleMouseMoveCanvasDrag(e)
      setCursorHover('grabbing')
      return
    }

    if (lineaReferenciaSeleccionada) {
      const circulo = encontrarCirculoArrastre(x, y, 8)
      if (circulo) {
        setCursorHover('grab')
        return
      }
      setCursorHover('default')
      return
    }

    if (medidaSeleccionada !== null) {
      const circulo = encontrarCirculoArrastreMedida(x, y, 8)
      if (circulo) {
        setCursorHover('grab')
        return
      }
      setCursorHover('default')
      return
    }

    if (medidaIndexParaLineaReferencia !== null && puntoALineaReferencia && !puntoBLineaReferenciaConfirmado) {
      const { x: xNormalizado, y: yNormalizado } = normalizarCoordenadas(x, y, canvas)
      setPuntoBLineaReferencia({ x: xNormalizado, y: yNormalizado })
      setCursorHover('crosshair')
      return
    }

    if (modoDibujo === 'puntoB' && puntoA && !puntoBConfirmado) {
      const { x: xNormalizado, y: yNormalizado } = normalizarCoordenadas(x, y, canvas)
      setPuntoBTemporal({ x: xNormalizado, y: yNormalizado })
      setCursorHover('crosshair')
      return
    }
    if (modoDibujo === 'circuloRadio' && centroCirculo && !radioConfirmado) {
      const { x: xNormalizado, y: yNormalizado } = normalizarCoordenadas(x, y, canvas)
      setPuntoRadioTemporal({ x: xNormalizado, y: yNormalizado })
      setCursorHover('crosshair')
      return
    }
    if (modoDibujo || medidaIndexParaLineaReferencia !== null) {
      setCursorHover('crosshair')
      return
    }

    const medidaIndex = encontrarMedidaCercana(x, y, 5)
    const lineaEncontrada = encontrarLineaReferenciaEspecifica(x, y, 5)
    
    if (medidaIndex !== null || lineaEncontrada) {
      setCursorHover('pointer')
    } else {
      setCursorHover('default')
    }
  }, [
    isDragging,
    arrastrandoPunto,
    lineaReferenciaSeleccionada,
    medidaSeleccionada,
    medidaIndexParaLineaReferencia,
    puntoALineaReferencia,
    puntoBLineaReferenciaConfirmado,
    modoDibujo,
    puntoA,
    puntoBConfirmado,
    centroCirculo,
    radioConfirmado,
    canvasRef,
    encontrarCirculoArrastre,
    encontrarCirculoArrastreMedida,
    encontrarMedidaCercana,
    encontrarLineaReferenciaEspecifica,
    handleMouseMoveCanvasDrag,
    setCursorHover,
    setPuntoBLineaReferencia,
    setPuntoBTemporal,
    setPuntoRadioTemporal,
  ])

  // Handler para mouse down
  const handleMouseDownCanvas = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (lineaReferenciaSeleccionada) {
      const circulo = encontrarCirculoArrastre(x, y, 8)
      if (circulo) {
        setIsDragging(true)
        setArrastrandoPunto(circulo)
        e.preventDefault()
        return
      }
    }

    if (medidaSeleccionada !== null) {
      const circulo = encontrarCirculoArrastreMedida(x, y, 8)
      if (circulo) {
        setIsDragging(true)
        setArrastrandoPunto(circulo)
        e.preventDefault()
        return
      }
    }

    if (medidaIndexParaLineaReferencia !== null) {
      return
    }

    if (modoDibujo) {
      return
    }

    const lineaEncontrada = encontrarLineaReferenciaEspecifica(x, y, 5)
    if (lineaEncontrada) {
      setLineaReferenciaSeleccionada(lineaEncontrada)
      setMedidaSeleccionada(null)
      setMedidaIndexParaLineaReferencia(null)
      setModoDibujo(null)
      e.preventDefault()
      return
    }

    const medidaIndex = encontrarMedidaCercana(x, y, 5)
    if (medidaIndex !== null) {
      setMedidaSeleccionada(medidaIndex)
      setLineaReferenciaSeleccionada(null)
      setMedidaIndexParaLineaReferencia(null)
      setModoDibujo(null)
      e.preventDefault()
      return
    }

    if (lineaReferenciaSeleccionada) {
      setLineaReferenciaSeleccionada(null)
    }
    if (medidaSeleccionada !== null) {
      setMedidaSeleccionada(null)
    }
  }, [
    lineaReferenciaSeleccionada,
    medidaSeleccionada,
    medidaIndexParaLineaReferencia,
    modoDibujo,
    encontrarCirculoArrastre,
    encontrarCirculoArrastreMedida,
    encontrarLineaReferenciaEspecifica,
    encontrarMedidaCercana,
    setIsDragging,
    setArrastrandoPunto,
    setLineaReferenciaSeleccionada,
    setMedidaSeleccionada,
    setMedidaIndexParaLineaReferencia,
    setModoDibujo,
    canvasRef,
  ])

  // Handler para mouse up
  const handleMouseUpCanvas = useCallback(() => {
    if (isDragging) {
      setIsDragging(false)
      setArrastrandoPunto(null)
    }
  }, [isDragging, setIsDragging, setArrastrandoPunto])

  // Handler para click
  const handleClickCanvas = useCallback((e) => {
    if (isDragging) {
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    if (medidaIndexParaLineaReferencia !== null) {
      const { x: xNormalizado, y: yNormalizado } = normalizarCoordenadas(x, y, canvas)

      if (!puntoALineaReferencia) {
        setPuntoALineaReferencia({ x: xNormalizado, y: yNormalizado })
      } else if (!puntoBLineaReferenciaConfirmado) {
        setPuntoBLineaReferencia({ x: xNormalizado, y: yNormalizado })
        setPuntoBLineaReferenciaConfirmado(true)
      }
      return
    }

    if (modoDibujo) {
      const { x: xNormalizado, y: yNormalizado } = normalizarCoordenadas(x, y, canvas)

      if (modoDibujo === 'puntoA') {
        setPuntoA({ x: xNormalizado, y: yNormalizado })
        setModoDibujo('puntoB')
        setPuntoBConfirmado(false)
      } else if (modoDibujo === 'puntoB') {
        setPuntoBTemporal({ x: xNormalizado, y: yNormalizado })
        setPuntoBConfirmado(true)
      } else if (modoDibujo === 'circuloCentro') {
        setCentroCirculo({ x: xNormalizado, y: yNormalizado })
        setModoDibujo('circuloRadio')
        setRadioConfirmado(false)
      } else if (modoDibujo === 'circuloRadio') {
        setPuntoRadioTemporal({ x: xNormalizado, y: yNormalizado })
        setRadioConfirmado(true)
      }
      return
    }

    const medidaIndex = encontrarMedidaCercana(x, y, 5)
    
    if (medidaIndex !== null) {
      const inputRef = inputRefs.current[medidaIndex]
      if (inputRef) {
        inputRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          inputRef.focus()
        }, 300)
      }
    }
  }, [
    isDragging,
    medidaIndexParaLineaReferencia,
    puntoALineaReferencia,
    puntoBLineaReferenciaConfirmado,
    modoDibujo,
    encontrarMedidaCercana,
    canvasRef,
    inputRefs,
    setPuntoA,
    setPuntoBTemporal,
    setPuntoBConfirmado,
    setCentroCirculo,
    setPuntoRadioTemporal,
    setRadioConfirmado,
    setPuntoALineaReferencia,
    setPuntoBLineaReferencia,
    setPuntoBLineaReferenciaConfirmado,
  ])

  return {
    handleMouseMoveCanvas,
    handleMouseDownCanvas,
    handleMouseUpCanvas,
    handleClickCanvas,
    handleMouseMoveCanvasDrag,
    encontrarMedidaCercana,
    encontrarLineaReferenciaEspecifica,
    encontrarCirculoArrastre,
    encontrarCirculoArrastreMedida,
  }
}
